import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BehaviorRange } from '../../../services/api/adminBehaviorService';
import { TaskySelect } from '../../tasky';
import { rangeForDays } from './format';
import OverviewTab from './OverviewTab';
import PagesTab from './PagesTab';
import FunnelsTab from './FunnelsTab';
import SessionsTab from './SessionsTab';
import SlowOperationsTab from './SlowOperationsTab';
import DailyReportTab from './DailyReportTab';

/**
 * admin「行動分析」タブ。サブタブと期間セレクタを持ち、各タブへ range を渡す。
 * サブタブは URL クエリ ?tab= で保持し、ユーザー/組織詳細からの「行動ログ」リンク
 * （?tab=sessions&user_id=…）を受ける。
 */

export type BehaviorTabKey = 'overview' | 'pages' | 'funnels' | 'sessions' | 'slow' | 'daily';

const TABS: { key: BehaviorTabKey; label: string }[] = [
  { key: 'overview', label: '概要' },
  { key: 'pages', label: 'ページ' },
  { key: 'funnels', label: 'ファネル' },
  { key: 'sessions', label: 'セッション' },
  { key: 'slow', label: '処理時間' },
  { key: 'daily', label: '日次レポート' },
];

const RANGE_OPTIONS: { days: number; label: string }[] = [
  { days: 7, label: '7日' },
  { days: 30, label: '30日' },
  { days: 90, label: '90日' },
];

const DEVICE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '全デバイス' },
  { value: 'desktop', label: 'PC' },
  { value: 'mobile', label: 'スマホ' },
  { value: 'tablet', label: 'タブレット' },
];

const isTabKey = (value: string | null): value is BehaviorTabKey =>
  TABS.some((t) => t.key === value);

const BehaviorAnalyticsPanel: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: BehaviorTabKey = isTabKey(tabParam) ? tabParam : 'overview';
  const [days, setDays] = useState<number>(30);
  const [device, setDevice] = useState<string>('');

  const range = useMemo<BehaviorRange>(() => {
    const base = rangeForDays(days);
    return device ? { ...base, device } : base;
  }, [days, device]);

  const initialUserId = Number(searchParams.get('user_id')) || undefined;
  const initialOrganizationId = Number(searchParams.get('organization_id')) || undefined;

  const handleTabChange = (tab: BehaviorTabKey): void => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="mb-8 rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-dark">行動分析</h2>
          <p className="text-xs text-warm-text-muted">
            全画面の表示・クリック・ステップを記録し、離脱ポイントと所要時間を可視化します
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-tasky-sm border border-warm-border p-0.5" role="group" aria-label="期間">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.days}
                type="button"
                onClick={() => setDays(option.days)}
                className={`rounded-tasky-sm px-3 py-1 text-xs font-medium ${
                  days === option.days ? 'bg-primary text-white' : 'text-warm-text-muted hover:text-dark'
                }`}
                aria-pressed={days === option.days}
              >
                {option.label}
              </button>
            ))}
          </div>
          <TaskySelect
            aria-label="デバイス"
            value={device}
            onChange={(e) => setDevice(e.target.value)}
            className="h-8 text-sm"
          >
            {DEVICE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TaskySelect>
        </div>
      </div>

      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-warm-border" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-warm-text-muted hover:text-dark'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <OverviewTab range={range} />}
      {activeTab === 'pages' && <PagesTab range={range} />}
      {activeTab === 'funnels' && <FunnelsTab range={range} />}
      {activeTab === 'sessions' && (
        <SessionsTab range={range} initialUserId={initialUserId} initialOrganizationId={initialOrganizationId} />
      )}
      {activeTab === 'slow' && <SlowOperationsTab range={range} />}
      {activeTab === 'daily' && <DailyReportTab />}
    </div>
  );
};

export default BehaviorAnalyticsPanel;
